'use client';

import { useEffect, useState } from 'react';
import PriceHistoryChart from './PriceHistoryChart';
import PredictionCard from './PredictionCard';
import GentrificationGauge from './GentrificationGauge';
import AnalyticsChat from './AnalyticsChat';
import NeighbourhoodPanel from './NeighbourhoodPanel';

// ── Types matching the API response ──────────────────────────────────────────

interface Prediction {
  last_known_price: number;
  last_known_year: number;
  year_1: number;
  year_3: number;
  year_5: number;
  confidence_low_5yr: number;
  confidence_high_5yr: number;
  annual_growth_pct: number;
  r2: number;
}

interface GentrificationData {
  index: number;
  stage: 'early-signs' | 'emerging' | 'mid-wave' | 'established';
  signals: {
    price_momentum: number;
    volume_change: number;
    new_build_ratio: number;
    leasehold_ratio: number;
  };
  transaction_count: number;
}

interface AnalyticsResponse {
  district: string;
  property_type: string;
  property_type_label: string;
  available_types: string[];
  price_history: {
    years: number[];
    median_prices: number[];
    yearly_pct: number[];
    avg_annual_pct: number;
    coefficients: {
      annual_growth: number;
      r2: number;
      volatility: number;
    };
  } | null;
  prediction: Prediction | null;
  gentrification: GentrificationData | null;
  similar_districts: Array<{ district: string; distance: number }>;
}

// ── Property type mapping (client-side) ──────────────────────────────────────

const APP_TO_LR: Record<string, string> = {
  detached: 'D',
  'semi-detached': 'S',
  terraced: 'T',
  flat: 'F',
  house: 'D',
};

function mapType(appType: string): string {
  return APP_TO_LR[appType.toLowerCase()] ?? 'S';
}

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  postcode: string;
  propertyType: string; // app-level type: 'flat', 'semi-detached', etc.
  askingPrice: number;
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-[#f3f0ea] ${className}`}
    />
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 p-1">
      <div className="space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-52 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
        <Skeleton className="h-44 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'charts' | 'neighbourhood' | 'chat';

// ── Component ────────────────────────────────────────────────────────────────

export default function PriceAnalyticsPanel({
  postcode,
  propertyType,
  askingPrice,
}: Props) {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('charts');

  useEffect(() => {
    const type = mapType(propertyType);
    fetch(`/api/analytics/${encodeURIComponent(postcode)}?type=${type}`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then((d) => setData(d as AnalyticsResponse))
      .catch((err) => setError(String(err)))
      .finally(() => setLoading(false));
  }, [postcode, propertyType]);

  if (loading) return <LoadingSkeleton />;

  if (error || !data) {
    return (
      <div className="rounded-xl border border-black/10 bg-[#faf8f4] p-6 text-center">
        <p className="text-sm font-medium text-[#7a7870]">
          Analytics unavailable for this area
        </p>
        <p className="mt-1 text-xs text-[#b8b5ae]">
          No Land Registry data found for this postcode district
        </p>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'charts', label: 'Price Analytics' },
    { key: 'neighbourhood', label: 'Neighbourhood' },
    { key: 'chat', label: 'Ask AI' },
  ];

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex gap-1 rounded-lg bg-[#f3f0ea] p-1">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              activeTab === key
                ? 'bg-white text-[#0f0f0d] shadow-sm'
                : 'text-[#7a7870] hover:text-[#3a3935]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Chart content */}
      {activeTab === 'charts' && (
        <div className="space-y-6">
          {/* Price history */}
          {data.price_history && (
            <PriceHistoryChart
              years={data.price_history.years}
              medianPrices={data.price_history.median_prices}
              yearlyPct={data.price_history.yearly_pct}
              district={data.district}
              propertyTypeLabel={data.property_type_label}
              askingPrice={askingPrice}
            />
          )}

          {/* Prediction */}
          {data.price_history && data.prediction && (
            <PredictionCard
              recentYears={data.price_history.years.slice(-8)}
              recentPrices={data.price_history.median_prices.slice(-8)}
              prediction={data.prediction}
              district={data.district}
              propertyTypeLabel={data.property_type_label}
            />
          )}

          {/* Gentrification */}
          <GentrificationGauge
            data={data.gentrification}
            district={data.district}
          />

          {/* Similar areas */}
          {data.similar_districts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-[#3a3935]">
                Similar Areas
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {data.similar_districts.map(({ district: d }) => (
                  <span
                    key={d}
                    className="rounded-full border border-black/10 bg-[#f3f0ea] px-2.5 py-1 text-xs font-medium text-[#3a3935]"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Neighbourhood content */}
      {activeTab === 'neighbourhood' && (
        <NeighbourhoodPanel
          postcode={postcode}
          offlineAnalytics={data.prediction && data.price_history ? {
            annual_growth_pct: data.prediction.annual_growth_pct,
            r2: data.prediction.r2,
            volatility: data.price_history.coefficients.volatility,
            gentrification_stage: data.gentrification?.stage ?? null,
            gentrification_index: data.gentrification?.index ?? null,
          } : undefined}
        />
      )}

      {/* Chat content */}
      {activeTab === 'chat' && (
        <AnalyticsChat
          context={{ postcode, propertyType, price: askingPrice }}
        />
      )}
    </div>
  );
}
